import React, { useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Box, Stack, SxProps, Tooltip,Typography, Avatar,Card,IconButton, Breadcrumbs, Icon, SvgIcon } from "@mui/material";
import { useAtom, useAtomValue } from "jotai";
import { baseAtom } from "../../stores/jotai/base";
import DoneIcon from '@mui/icons-material/Done';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import calculateNameInitial from "../../util/calculateNameInitial";
import { useTheme,Theme } from '@mui/material/styles';
import HomeOverviewSkeleton from "./Skeleton/HomeOverviewSkeleton";
import { selectedGroupAtom } from "../../stores/jotai/orgTree/selectedGroup";
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { useQueryClient } from "@tanstack/react-query";
import { discoverySessionAtom } from "../../stores/jotai/discovery-session";
import { OrgIcon } from "./icon/OrgIcon";
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
interface UserRole {
  isInstructor: string;
  isMentor: string;
  isObserver: string;
  isTeamMember: string;
  isCohortAdmin: string;
  isSystemAdministrator:string;
}
const allRoles: UserRole = {
  isInstructor: "Instructor",
  isMentor: "Mentor",
  isObserver: "Observer",
  isTeamMember: "TeamMember",
  isCohortAdmin: "CohortAdministrator",
  isSystemAdministrator: "SystemAdministrator"
};


const USER_INFO_TITLE_STYLES: SxProps = {
    fontSize: "15px",
    fontFamily: "Inter, sans-serif",
    fontWeight: 700,
    color: "rgba(0, 0, 0, 0.7)",
}
const USER_INFO_STYLES: SxProps = {
    fontSize: "15px",
    fontFamily: "Inter, sans-serif",
    fontWeight: 500,
    color: "rgba(0, 0, 0, 0.8)",
}

const roleColors = {
  TeamMember: "#82B184",
  Mentor: "#5B93D3",
  Observer: "#9A9A9A",
  Instructor: "#FFEE58",
  CohortAdministrator: "#D76969",
  SystemAdministrator:'red'
};

const CONTAINER_STYLES = (theme: Theme) => ({
    boxShadow: "0 0 0 0.67px rgba(0, 0, 0, 0.09), 0 34px 20px rgba(0, 0, 0, 0.024)",
    backdropFilter: "blur(40px)", // Add background blur
    WebkitBackdropFilter: "blur(40px)", // For Safari compatibility
    transition: `box-shadow 0.15s ${theme.custom.timingFunction1}`,
    overflow: "hidden",
    ":hover": {
      boxShadow: `0 0 0 3px ${theme.custom.homeCardBoldGray}`,
    },
  });

  function findGroupTypes(hierarchy, targetParentID, targetChildID) {
    let groupDetails = [];
    function traverseWithLoop(groups) {
      let groupsToProcess = groups?.map((group) => ({
        currentGroup: group,
        pathToCurrentGroup: [],
      }));
      while (groupsToProcess?.length > 0) {
        // Get the last group to process.
        const { currentGroup, pathToCurrentGroup } = groupsToProcess.pop();
        // Create a new path by copying the current path.
        let updatedPath = [...pathToCurrentGroup];
        // Add the current group's details to the path.
        if (
          currentGroup.group &&
          currentGroup.group.groupType &&
          currentGroup.group.name
        ) {
          updatedPath.push({
            groupType: currentGroup.group.groupType,
            name: currentGroup.group.name,
          });
        }
        // Check if any children match the target.
        for (let childGroup of currentGroup.children || []) {
          if (
            childGroup.parentID === targetParentID &&
            childGroup.childID === targetChildID
          ) {
            groupDetails = [...updatedPath]; // Copy the current path to the result.
            groupDetails.push({
              groupType: childGroup.group.groupType,
              name: childGroup.group.name,
            });
            return; // Stop processing since we found the target.
          }
          // Add the child group to the list of groups to process.
          groupsToProcess.push({
            currentGroup: childGroup,
            pathToCurrentGroup: updatedPath,
          });
        }
      }
    }
    // Start the traversal from the top-level groups.
    traverseWithLoop(hierarchy);
    return groupDetails;
  }


const userInfoDetails = (user, org, cohort, team, foundRoles, getOrgBreadCrumbDetails) => {
    const roles = foundRoles?.join(" ");
    const orgDetails = getOrgBreadCrumbDetails
        ?.map((folder) => `/ ${folder.name}`)
        .join("");
    return (
        "Name: " + user.name + "\n" +
        "Role: " + roles + "\n" +
        "Username: " + user.username + "\n" +
        "email: " + user.email + "\n" +
        "Organization: " + org.name + " " + orgDetails + "\n" +
        "Cohort: " + cohort.name + "\n" +
        "Team: " + team.name
    );
};
  
const HomeOverview = () => {
    const base = useAtomValue(baseAtom);
    const [copiedText, setCopiedText] = useState(false);
    const selectedGroupInfo = useAtomValue(selectedGroupAtom)
    const discoverySession = useAtomValue(discoverySessionAtom);

    const queryClient = useQueryClient();
    const orgTreeData = queryClient.getQueryData([
        "orgtree",
        {
        groupID: base?.team?.groupID,
        orgID: base?.team?.orgID,
        sessionID: discoverySession?.sessionID,
        },
    ]);

    const theme = useTheme();
    const { user, cohort, org, team, roleSnapshot  } = base;
    const foundRoles = Object.keys(allRoles)?.filter(key => roleSnapshot.hasOwnProperty(key) && roleSnapshot[key])?.map(key => allRoles[key]);
    const nameInitials = calculateNameInitial(user.name)
    const getRoleColor = (roleName) => roleColors[roleName] || '#9A9A9A';
    const handleCopy = () => {
      setCopiedText(true);
      const timeoutId = setTimeout(() => {  
        setCopiedText(false);  
      }, 2000);  
      return () => clearTimeout(timeoutId);
    };
    const isBaseLoading =  Object.keys(base.user || {})?.length === 0 ;
    const getOrgBreadCrumbDetails = findGroupTypes(
        // eslint-disable-next-line
        // @ts-ignore
        orgTreeData?.hierarchy,
        selectedGroupInfo?.parentID,
        selectedGroupInfo?.childID
     )?.filter((data) => data.groupType === "Folder");
    const userInfo = userInfoDetails(user,org,cohort,team,foundRoles,getOrgBreadCrumbDetails)
    return (
        <>
        {
            isBaseLoading ? <HomeOverviewSkeleton/> : 
             <Stack direction="row" alignItems="center" justifyContent="center">
             <Stack direction="row" 
                 sx={{
                     ...CONTAINER_STYLES(theme),
                     backgroundColor: "white",
                     display: "flex",
                     borderRadius: "4px",
                     p: "10px 12px",
                     minWidth: "636px",
                     boxShadow:
                         "0 0 0 0.67px rgba(0, 0, 0, 0.09), 0 34px 20px rgba(0, 0, 0, 0.024)",
                     backdropFilter: "blur(40px)", // Add background blur
                     WebkitBackdropFilter: "blur(40px)", // For Safari compatibility
                     transition: `box-shadow 0.15s ${theme.custom.timingFunction1}`,
                     overflow: "hidden",
                     ":hover": {
                         boxShadow: `0 0 0 3px ${theme.custom.homeCardBoldGray}`,
                     },
                 }}
             >
                 <Avatar
                     sx={{
                         width: 160,
                         height: 160,
                         borderRadius: 1,
                         fontSize: "5rem",
                         fontWeight: "bold",
                     }}
                     alt="User Profile Picture"
                 >
                     {nameInitials}
                 </Avatar>
                 <Box
                     sx={{
                         display: "flex",
                         flexDirection: "column",
                         ml: "12px",
                         justifyContent: "space-between",
                         width: "100%",
                     }}
                 >
                     <Stack direction="column">
                         <Stack
                             direction="row"
                             mb="4px"
                             justifyContent="space-between"
                         >
                             <Typography
                                 variant="h5"
                                 sx={{ fontWeight: "bold", color: "#374151" }}
                             >
                                 {user.name}
                             </Typography>
                             <CopyToClipboard
                                 text={userInfo}
                                 onCopy={handleCopy}
                             >
                                 <Tooltip
                                     title={
                                         copiedText
                                             ? "Copied!"
                                             : "Copy user information"
                                     }
                                     placement="top"
                                 >
                                     <IconButton
                                         sx={{
                                             backgroundColor: !copiedText
                                                 ? "white"
                                                 : "#bbf7d0",
                                         }}
                                     >
                                         {!copiedText ? (
                                             <ContentCopyIcon />
                                         ) : (
                                             <DoneIcon
                                                 sx={{ color: "#15803d" }}
                                             />
                                         )}
                                     </IconButton>
                                 </Tooltip>
                             </CopyToClipboard>
                         </Stack>
                         <Stack direction="row" gap={1} mb={"4px"}>
                             {foundRoles?.map((role: string) => (
                                 <Box
                                     key={role}
                                     sx={{
                                         display: "inline-flex",
                                         alignItems: "center",
                                         justifyContent: "center",
                                         borderRadius: "4px",
                                         backgroundColor: getRoleColor(
                                             role
                                         ),
                                         color:
                                             role === "Instructor"
                                                 ? "rgba(0, 0, 0, 0.8)"
                                                 : "#ffff",
                                         padding: "1px 3px",
                                         fontSize: "0.875rem",
                                         fontWeight: 500,
                                     }}
                                 >
                                     {role}
                                 </Box>
                             ))}
                         </Stack>
                         <Stack direction="row">
                             <Typography sx={USER_INFO_TITLE_STYLES}>
                                 Username:
                             </Typography>
                             <Typography ml={1} sx={USER_INFO_STYLES}>
                                 {user?.username} • {user?.email}
                             </Typography>
                         </Stack>
 
                         <Box
                             sx={{
                                 borderBottom: "1px solid #c2c2c2",
                                 my: "2px",
                             }}
                         ></Box>
                     </Stack>
                     <Stack direction="column">
                         <Stack direction="row"><OrgIcon
                               sx={{ fontSize: 21, mr: 0.6 }}
                              />
                             <Typography sx={USER_INFO_TITLE_STYLES}>
                             
                                 Org:
                             </Typography>
                             <Box ml={1} sx={USER_INFO_STYLES}>
                             <Breadcrumbs sx={{ backgroundColor: 'transparent' }}>
                             <Typography sx={{ display: 'flex', alignItems: 'center', ...USER_INFO_STYLES }}>
                                {org?.name}
                             </Typography>
                             {
                                getOrgBreadCrumbDetails?.map((folder)=> (
                                <Typography key={folder.name} sx={{ display: 'flex', alignItems: 'center',...USER_INFO_STYLES  }}>
                                <FolderOutlinedIcon sx={{ mr: 0.6, fontSize: 21 }} fontSize="inherit" />
                                {folder?.name}
                             </Typography>))
                             }
                            </Breadcrumbs>
                            </Box>
                         </Stack>
                         <Stack direction="row">
                         <FolderSharedOutlinedIcon sx={{ mr: 0.6, fontSize: 21 }} fontSize="inherit" />
                             <Typography sx={USER_INFO_TITLE_STYLES}>
                                 Cohort:
                             </Typography>
                             <Typography ml={1} sx={{ display: 'flex', alignItems: 'center',...USER_INFO_STYLES  }}>
                           
                                
                                {cohort?.name}
                             
                             </Typography>
                         </Stack>
                         <Stack direction="row">
                         <SupervisorAccountOutlinedIcon sx={{ mr: 0.6, fontSize: 21 }} fontSize="inherit" />
                             <Typography sx={USER_INFO_TITLE_STYLES}>
                                 Team:
                             </Typography>
                             <Typography ml={1} sx={{ display: 'flex', alignItems: 'center',...USER_INFO_STYLES  }}>
                               
                                {team?.name}
                           </Typography>
                           </Stack>
                     </Stack>
                 </Box>
             </Stack>
         </Stack>
        }
        </>
    );
};

export default HomeOverview;
